/**
 * ZoneDeltaCard — per-Rachio-zone moisture gap vs the 48% mow target.
 *
 * Drops onto /irrigation directly below the OptimalWindowCard so the
 * decision flow is:
 *   1. Is now a good time to fire?  (OptimalWindowCard)
 *   2. WHICH zones need it most?    (this card)
 *
 * Backed by GET /api/dashboard/probe-deltas. Empty states explain how to
 * finish the probe→zone mapping when nothing is wired yet — critical for
 * users with multi-probe Ecowitt setups but no zone_overrides config.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Activity, AlertTriangle, CheckCircle2, Droplets, Settings, RefreshCw, Plug, Wand2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { API_URL } from '../config/api';

const STATUS = {
  very_dry:  { color: 'text-rose-500',    bg: 'bg-rose-500/10',    border: 'border-rose-500/40',    badge: 'DRY · ACT' },
  dry:       { color: 'text-amber-500',   bg: 'bg-amber-500/10',   border: 'border-amber-500/40',   badge: 'TRENDING DRY' },
  on_target: { color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', badge: 'ON TARGET' },
  wet:       { color: 'text-sky-500',     bg: 'bg-sky-500/10',     border: 'border-sky-500/40',     badge: 'WET' },
  very_wet:  { color: 'text-purple-500',  bg: 'bg-purple-500/10',  border: 'border-purple-500/40',  badge: 'SATURATED' },
};

function Pill({ status }) {
  const s = STATUS[status] || STATUS.on_target;
  return (
    <span className={`text-[9px] uppercase tracking-widest font-bold ${s.color} ${s.bg} border ${s.border} rounded-full px-2 py-0.5`}>
      {s.badge}
    </span>
  );
}

export const ZoneDeltaCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/probe-deltas`, { withCredentials: true });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyAll = useCallback(async () => {
    setApplying(true);
    setApplyResult(null);
    try {
      const { data: res } = await axios.post(
        `${API_URL}/api/dashboard/apply-recommendations`,
        {},
        { withCredentials: true },
      );
      setApplyResult(res);
      await refresh();
      // Auto-clear the receipt after a beat so the card returns to normal
      setTimeout(() => setApplyResult(null), 6000);
    } catch (e) {
      setApplyResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setApplying(false);
    }
  }, [refresh]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !data) {
    return (
      <Card className="mb-4" data-testid="zone-delta-card-loading">
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          Computing per-zone deltas…
        </CardContent>
      </Card>
    );
  }
  if (err) return (
    <Card className="mb-4 border-amber-500/40" data-testid="zone-delta-card-error">
      <CardContent className="py-4 text-sm text-amber-700 dark:text-amber-300">
        Could not load per-zone deltas: {err}
      </CardContent>
    </Card>
  );
  if (!data) return null;

  const target = data.target_moisture_pct;
  const zones = data.zones || [];
  const unmappedZones = data.unmapped_zones || [];
  const unmappedProbes = data.unmapped_probes || [];

  return (
    <Card className="mb-4 border-sky-500/30 bg-sky-500/[0.02]" data-testid="zone-delta-card">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-baseline justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-sky-500" />
            <span className="text-sm font-semibold">Per-zone delta vs {target}% target</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Mow-window target</span>
            <Button size="icon" variant="ghost" onClick={refresh} disabled={loading} data-testid="zone-delta-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Mapped zones */}
        {zones.length > 0 && (
          <>
            {/* Apply All Recommendations — 1-tap closed-loop CTA.
                Only enabled when at least one zone has a non-zero
                adjust_pct AND a recommended_runtime_minutes. */}
            {(() => {
              const adjustable = zones.filter(
                (z) => z.recommended_runtime_minutes != null && (z.adjust_pct ?? 0) !== 0,
              );
              if (adjustable.length === 0) return null;
              return (
                <div className="mb-3 flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={applyAll}
                    disabled={applying}
                    data-testid="zone-delta-apply-all"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  >
                    <Wand2 className={`w-3.5 h-3.5 mr-1.5 ${applying ? 'animate-pulse' : ''}`} />
                    {applying ? 'Applying…' : `Apply ${adjustable.length} recommendation${adjustable.length === 1 ? '' : 's'}`}
                  </Button>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    1-tap closed-loop
                  </span>
                </div>
              );
            })()}

            {applyResult && (
              <div
                data-testid="zone-delta-apply-result"
                className={`mb-3 rounded-lg p-2.5 text-xs leading-snug ${
                  applyResult.error
                    ? 'bg-rose-950/40 border border-rose-900 text-rose-300'
                    : 'bg-emerald-950/40 border border-emerald-900 text-emerald-200'
                }`}
              >
                {applyResult.error ? (
                  <>Failed: {applyResult.error}</>
                ) : (
                  <>
                    Updated <b>{applyResult.applied_count}</b> zone
                    {applyResult.applied_count === 1 ? '' : 's'} across
                    <b> {applyResult.schedules_touched}</b> schedule
                    {applyResult.schedules_touched === 1 ? '' : 's'}.
                    {applyResult.zones?.slice(0, 3).map((r) => (
                      <div key={r.rachio_zone_id} className="text-emerald-300/80 mt-0.5">
                        • {r.name}: {r.old_runtime_minutes}m → <b>{r.new_runtime_minutes}m</b>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          <div className="space-y-2" data-testid="zone-delta-mapped">
            {zones.map(z => {
              const s = STATUS[z.status] || STATUS.on_target;
              const deltaSign = z.delta_pts > 0 ? '+' : '';
              const adjustSign = z.adjust_pct > 0 ? '+' : '';
              return (
                <div key={z.rachio_zone_id}
                     data-testid={`zone-delta-row-${z.rachio_zone_id}`}
                     className={`bg-background border ${s.border} rounded-lg p-3`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <Droplets className={`w-4 h-4 ${s.color}`} />
                      <div>
                        <div className="text-sm font-semibold">{z.name}</div>
                        <div className="text-[10px] text-muted-foreground">
                          Ch {z.probe_channel}{z.sensor_model ? ` · ${z.sensor_model}` : ''}
                          {z.schedules?.length ? ` · ${z.schedules.join(', ')}` : ''}
                        </div>
                      </div>
                    </div>
                    <Pill status={z.status} />
                  </div>

                  {/* Delta bar — visualizes how far from 48% we are */}
                  <div className="flex items-baseline gap-3 mt-2">
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between text-[11px] mb-0.5">
                        <span className="text-muted-foreground">Current</span>
                        <span className={`font-bold tabular-nums ${s.color}`}>
                          {z.current_moisture_pct}% <span className="text-muted-foreground text-[10px]">({deltaSign}{z.delta_pts} pts)</span>
                        </span>
                      </div>
                      <DeltaBar current={z.current_moisture_pct} target={target} />
                    </div>
                  </div>

                  {/* Recommendation row */}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/40 text-[11px]">
                    <span className="text-muted-foreground">{z.advice}</span>
                    {z.current_runtime_minutes != null && z.recommended_runtime_minutes != null && z.adjust_pct !== 0 && (
                      <span className={`font-bold ${z.adjust_pct > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}
                            data-testid={`zone-delta-runtime-${z.rachio_zone_id}`}>
                        {z.current_runtime_minutes} → {z.recommended_runtime_minutes} min
                        <span className="ml-1 text-[10px]">({adjustSign}{z.adjust_pct}%)</span>
                      </span>
                    )}
                    {z.adjust_pct === 0 && (
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                        Keep at {z.current_runtime_minutes ?? '—'} min
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}

        {/* Empty / unmapped helpers */}
        {zones.length === 0 && (
          <div className="bg-background border border-border rounded-lg p-4 text-center"
               data-testid="zone-delta-empty">
            <Plug className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
            <div className="text-sm font-semibold mb-1">No zones mapped to probes yet</div>
            <div className="text-xs text-muted-foreground">
              Map each Rachio zone to its closest WH51/WH52 channel in Property
              Settings → Zone Overrides. Once mapped, every zone will get its
              own per-probe runtime advice here.
            </div>
          </div>
        )}

        {(unmappedZones.length > 0 || unmappedProbes.length > 0) && (
          <div className="mt-3 pt-3 border-t border-border/40 grid grid-cols-2 gap-3 text-[11px]"
               data-testid="zone-delta-unmapped">
            {unmappedZones.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                  Zones without a probe
                </div>
                {unmappedZones.slice(0, 4).map(z => (
                  <div key={z.rachio_zone_id} className="flex items-center gap-1 text-muted-foreground">
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                    <span>{z.name}</span>
                  </div>
                ))}
              </div>
            )}
            {unmappedProbes.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                  Probes without a zone
                </div>
                {unmappedProbes.slice(0, 4).map(p => (
                  <div key={p.channel} className="flex items-center gap-1 text-muted-foreground">
                    <CheckCircle2 className="w-3 h-3 text-sky-500" />
                    <span>Ch {p.channel} · {p.moisture_pct}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

function DeltaBar({ current, target }) {
  // Render a horizontal scale 0-100% with a vertical target tick + a colored
  // current marker. Width-clamped so a 90% reading doesn't overflow.
  const cur = Math.max(0, Math.min(100, current));
  const tgt = Math.max(0, Math.min(100, target));
  return (
    <div className="relative h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
      {/* Current value bar (filled) */}
      <div
        className="absolute left-0 top-0 h-full bg-sky-500/40"
        style={{ width: `${cur}%` }}
      />
      {/* Target tick */}
      <div
        className="absolute top-0 h-full w-0.5 bg-emerald-500"
        style={{ left: `${tgt}%` }}
      />
    </div>
  );
}
